import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../config';

interface DeveloperProfile {
  uid: string;
  email: string;
  displayName?: string;
  earnings_balance: number;
  company?: string;
  paypalEmail?: string;
}

interface AuthContextType {
  user: User | null;
  profile: DeveloperProfile | null;
  isProfileComplete: boolean;
  missingProfileFields: string[];
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  fetchProfile: (userId: string, userEmail: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  isProfileComplete: false,
  missingProfileFields: [],
  loading: true,
  error: null,
  signOut: async () => { },
  fetchProfile: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<DeveloperProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchingProfileRef = useRef(false);
  const loadingResolvedRef = useRef(false);

  const resolveLoading = useCallback(() => {
    if (!loadingResolvedRef.current) {
      loadingResolvedRef.current = true;
      setLoading(false);
    }
  }, []);

  const pause = useCallback((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)), []);

  const buildDeveloperProfile = useCallback(async (userId: string, userEmail: string): Promise<DeveloperProfile | null> => {
    const [{ data: developerRow, error: developerError }, { data: profileRow, error: profileError }] = await Promise.all([
      supabase
        .from('developers')
        .select('earnings_balance, payout_details')
        .eq('id', userId)
        .maybeSingle(),
      supabase
        .from('profiles')
        .select('display_name')
        .eq('id', userId)
        .maybeSingle(),
    ]);

    if (developerError) {
      throw developerError;
    }

    if (profileError) {
      throw profileError;
    }

    if (!developerRow) {
      return null;
    }

    const payoutDetails = ((developerRow as any).payout_details || {}) as {
      company?: string;
      paypal_email?: string;
    };

    return {
      uid: userId,
      email: userEmail,
      displayName: (profileRow as any)?.display_name || userEmail.split('@')[0] || 'Developer',
      earnings_balance: (developerRow as any).earnings_balance || 0,
      company: payoutDetails.company || '',
      paypalEmail: payoutDetails.paypal_email || '',
    };
  }, []);

  const getMissingProfileFields = useCallback((nextProfile: DeveloperProfile | null) => {
    if (!nextProfile) return ['display name', 'company', 'PayPal email'];
    const missing: string[] = [];
    if (!nextProfile.displayName?.trim()) missing.push('display name');
    if (!nextProfile.company?.trim()) missing.push('company');
    if (!nextProfile.paypalEmail?.trim()) missing.push('PayPal email');
    return missing;
  }, []);

  const ensureDeveloperProvisioned = useCallback(async (userId: string, userEmail: string) => {
    const { data: authUserData } = await supabase.auth.getUser();
    const currentType = authUserData.user?.user_metadata?.account_type;

    if (currentType !== 'developer' && currentType !== 'both') {
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          ...(authUserData.user?.user_metadata || {}),
          account_type: 'developer',
          display_name: authUserData.user?.user_metadata?.display_name || userEmail.split('@')[0] || 'Developer',
        },
      });

      if (updateError) {
        console.warn('[AdMesh Developer Auth] Failed to sync account_type metadata:', updateError);
      }
    }

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const nextProfile = await buildDeveloperProfile(userId, userEmail);

      if (nextProfile) {
        setProfile(nextProfile);
        return;
      }

      await pause(400);
    }

    console.warn('[AdMesh Developer Auth] Developer record still missing after trigger sync.');
  }, [buildDeveloperProfile, pause]);

  const fetchProfile = useCallback(async (userId: string, userEmail: string) => {
    if (fetchingProfileRef.current) return;
    fetchingProfileRef.current = true;

    try {
      const nextProfile = await buildDeveloperProfile(userId, userEmail);

      if (nextProfile) {
        setProfile(nextProfile);
      } else {
        // Set minimal profile immediately
        setProfile({
          uid: userId,
          email: userEmail,
          displayName: userEmail.split('@')[0] || 'Developer',
          earnings_balance: 0,
          company: '',
          paypalEmail: '',
        });

        await ensureDeveloperProvisioned(userId, userEmail);
      }
    } catch (err: any) {
      console.error('[AdMesh Developer Auth] Error loading developer profile:', err);
      setError('Error loading profile');
      setProfile({
        uid: userId,
        email: userEmail,
        displayName: '',
        earnings_balance: 0,
        company: '',
        paypalEmail: '',
      });
    } finally {
      fetchingProfileRef.current = false;
    }
  }, [buildDeveloperProfile, ensureDeveloperProvisioned]);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    loadingResolvedRef.current = false;
  }, []);

  useEffect(() => {
    let mounted = true;

    // Hard safety net — if nothing resolves in 4s, show the app anyway
    const timeoutId = setTimeout(() => {
      if (mounted) resolveLoading();
    }, 4000);

    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.warn('[AdMesh Developer Auth] getSession error:', sessionError.message);
        }

        if (!mounted) return;

        if (initialSession?.user) {
          setUser(initialSession.user);
          resolveLoading(); // ← Resolve IMMEDIATELY, don't wait for profile
          fetchProfile(initialSession.user.id, initialSession.user.email || ''); // ← No await!
        } else {
          setUser(null);
          setProfile(null);
          resolveLoading();
        }
      } catch (err: any) {
        console.error('[AdMesh Developer Auth] Init error:', err?.message || err);
        if (mounted) resolveLoading();
      } finally {
        clearTimeout(timeoutId);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        if (newSession?.user) {
          setUser(newSession.user);
          resolveLoading(); // ← Resolve IMMEDIATELY
          fetchProfile(newSession.user.id, newSession.user.email || ''); // ← No await!
        } else {
          setUser(null);
          setProfile(null);
          resolveLoading();
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchProfile, resolveLoading]);

  const missingProfileFields = getMissingProfileFields(profile);
  const isProfileComplete = missingProfileFields.length === 0;

  return (
    <AuthContext.Provider value={{ user, profile, isProfileComplete, missingProfileFields, loading, error, signOut: handleSignOut, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

